import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Loader2, BookMarked, Sparkles, Link2, HelpCircle, Landmark, Compass, Lock, Crown } from "lucide-react";
import { Link } from "react-router-dom";
import { getStudy } from "../api";
import { usePremium } from "../context/PremiumContext";

function Section({ icon: Icon, label, children }) {
  return (
    <div className="mb-6">
      <p className="font-ui mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-primary">
        <Icon className="h-3.5 w-3.5" /> {label}
      </p>
      {children}
    </div>
  );
}

export default function StudyPanel({ open, onOpenChange, book, chapter, bookName }) {
  const { isPremium } = usePremium();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!open || !isPremium) return;
    setData(null); setError(false); setLoading(true);
    getStudy(book, chapter)
      .then(setData)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [open, book, chapter, isPremium]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="font-ui w-full max-w-md overflow-y-auto" data-testid="study-panel">
        <SheetHeader>
          <SheetTitle className="font-serif-display text-2xl">
            Study · {bookName} {chapter}
          </SheetTitle>
          <SheetDescription className="sr-only">AI-generated Bible study notes for this chapter</SheetDescription>
        </SheetHeader>

        {!isPremium ? (
          <div className="mt-10 flex flex-col items-center gap-4 text-center" data-testid="study-gate">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/15 text-primary">
              <Lock className="h-6 w-6" />
            </span>
            <p className="font-serif-display text-xl">AI Study Notes are Premium</p>
            <p className="text-sm text-muted-foreground">Unlock summaries, themes, cross-references and reflection questions for every chapter.</p>
            <Link to="/premium" data-testid="study-upgrade"
              className="mt-2 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground">
              <Crown className="h-4 w-4" /> View Premium
            </Link>
          </div>
        ) : loading ? (
          <div className="mt-10 flex flex-col items-center gap-3 text-center">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Preparing study notes…<br />(first time for a chapter takes a few seconds)</p>
          </div>
        ) : error ? (
          <p className="mt-8 text-sm text-muted-foreground">Couldn’t load study notes right now. Please try again shortly.</p>
        ) : data ? (
          <div className="mt-6">
            <Section icon={Sparkles} label="Overview">
              <p className="reading-text text-sm leading-relaxed text-foreground/90">{data.summary}</p>
            </Section>

            {data.themes?.length > 0 && (
              <Section icon={BookMarked} label="Key Themes">
                <div className="flex flex-wrap gap-2">
                  {data.themes.map((t, i) => (
                    <span key={i} className="rounded-full bg-muted px-3 py-1 text-xs">{t}</span>
                  ))}
                </div>
              </Section>
            )}

            {data.key_verse && (
              <Section icon={Compass} label="Key Verse">
                <p className="text-sm"><span className="font-semibold text-primary">Verse {data.key_verse.verse}</span> — {data.key_verse.reason}</p>
              </Section>
            )}

            {data.context && (
              <Section icon={Landmark} label="Context">
                <p className="reading-text text-sm leading-relaxed text-foreground/90">{data.context}</p>
              </Section>
            )}

            {data.cross_references?.length > 0 && (
              <Section icon={Link2} label="Cross References">
                <ul className="space-y-2">
                  {data.cross_references.map((c, i) => (
                    <li key={i} className="text-sm">
                      <span className="font-semibold text-primary">{c.ref}</span>
                      <span className="text-foreground/80"> — {c.note}</span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {data.questions?.length > 0 && (
              <Section icon={HelpCircle} label="Reflection">
                <ol className="list-decimal space-y-2 pl-5">
                  {data.questions.map((q, i) => (
                    <li key={i} className="reading-text text-sm leading-relaxed text-foreground/90">{q}</li>
                  ))}
                </ol>
              </Section>
            )}

            {data.application && (
              <div className="rounded-2xl bg-primary/10 p-5">
                <p className="font-ui mb-1 text-xs font-semibold uppercase tracking-[0.18em] text-primary">Apply it</p>
                <p className="reading-text text-sm leading-relaxed text-foreground/90">{data.application}</p>
              </div>
            )}
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
